import { z } from "zod";

export const BaseAttrsSchema = z.object({
	audienceId: z.coerce.number(),
	widgetType: z.enum(["fund-page", "fund-explorer"]),
});

export type BaseDataset = z.infer<typeof BaseAttrsSchema>;

export const BaseSRPSectionConfigSchema = z.object({
	partId: z.coerce.number(),
	shareClassesPartId: z.coerce.number().optional(),
	apiId: z.coerce.number().optional(),
});
export type BaseSRPSectionConfig = z.infer<typeof BaseSRPSectionConfigSchema>;

export const ShareClassSchema = z.object({
	id: z.number(),
	letter: z.string(),
	currency: z.string(),
	type: z.string(),
	isHedged: z.boolean(),
	isin: z.string(),
	launchDate: z.coerce.date(),
});
export type ShareClass = z.infer<typeof ShareClassSchema>;

export const ShareClassesAPIResponseSchema = z.object({
	data: z.object({
		shareClasses: ShareClassSchema.array(),
	}),
});
export type ShareClassesAPIResponse = z.infer<
	typeof ShareClassesAPIResponseSchema
>;

export const BreadcrumbLinkSchema = z.object({
	type: z.literal("link"),
	icon: z.string().optional(),
	label: z.string(),
	path: z.string(),
	args: z.record(z.string()).optional(),
});
export type BreadcrumbLink = z.infer<typeof BreadcrumbLinkSchema>;

export const BreadcrumbListSchema = z.object({
	type: z.literal("list"),
	items: BreadcrumbLinkSchema.array(),
});
export type BreadcrumbList = z.infer<typeof BreadcrumbListSchema>;

export const BreadcrumbSchema = z.discriminatedUnion("type", [
	BreadcrumbLinkSchema,
	BreadcrumbListSchema,
]);
export type Breadcrumb = z.infer<typeof BreadcrumbSchema>;

export const ConfigPartSchema = z.object({
	config: z.string(),
});
export type ConfigPart = z.infer<typeof ConfigPartSchema>;

export const CitywireImagesSchema = z.object({
	citywireRating: z.record(z.string()),
});
export type CitywireImages = z.infer<typeof CitywireImagesSchema>;

export const PeriodSchema = z.enum(["1y", "3y", "5y", "10y", "sl"]);
export type Period = z.infer<typeof PeriodSchema>;

export const PerfAPIResponseSchema = z.object({
	share_class_info: z.object({
		share_class_name: z.string(),
		share_class_long_name: z.string(),
		share_class_short_name: z.string(),
		launch_date: z.coerce.date(),
		significant_performance_date: z.string(),
		valid_periods: z.array(z.tuple([PeriodSchema, z.string()])),
	}),
	share_class_plots: z.array(
		z.object({
			price_type: z.string(),
			calculation_basis: z.string(),
			// Each data point is a tuple of [timestamp, value]
			data: z.array(z.tuple([z.number(), z.number()])),
			// Gaps are arrays of date pairs, e.g. [["2020-02-25","2020-02-28"], ...]
			gaps: z.array(z.array(z.string())),
		}),
	),
	index_plots: z.array(
		z.object({
			data: z.array(z.tuple([z.number(), z.number()])),
			gaps: z.array(z.array(z.string())),
			name: z.string(),
			id: z.number(),
			is_sector: z.boolean(),
		}),
	),
	as_at_date: z.coerce.date(),
	start_date: z.coerce.date(),
	end_date: z.coerce.date(),
	earliest_start_date: z.coerce.date(),
	latest_start_date: z.coerce.date().nullable(),
	note: z.string(),
	period: z.array(z.string()).optional(),
});

export type PerfAPIResponse = z.infer<typeof PerfAPIResponseSchema>;

const LitAPIDataSchema = z.object({
	set_id: z.number(),
	display_name: z.string(),
	settings: z.record(z.any()),
	rank: z.number(),
	document_count: z.number(),
	lit_types: z.array(z.number()),
	share_class_display: z.number().nullable(),
	fund_display: z.any().nullable(),
	show_history: z.boolean(),
	date_selector_frequency: z.any().nullable(),
	date_from: z.any().nullable(),
	tab_title: z.string(),
	document_naming_rule: z.string(),
	show_document_date: z.boolean(),
	document_date_source: z.any().nullable(),
	document_date_format: z.any().nullable(),
	show_document_note: z.boolean(),
	show_document_icon: z.boolean(),
	show_empty_tab: z.boolean(),
	empty_tab_message: z.any().nullable(),
	order_by: z.string(),
	tab_note: z.any().nullable(),
	show_language_label: z.boolean(),
});
export type LitAPIData = z.infer<typeof LitAPIDataSchema>;

export const LitAPIResponseSchema = z.object({
	data: z.array(LitAPIDataSchema),
	settings: z.record(z.any()),
	name: z.string(),
});
export type LitAPIResponse = z.infer<typeof LitAPIResponseSchema>;

export const DocumentSchema = z.object({
	id: z.number(),
	lit_type_id: z.number(),
	date: z.coerce.date(),
	display_name: z.string(),
	category: z.string(),
	secondary_category: z.string(),
	description: z.string().nullable(),
	description_phrase: z.string(),
	language_aware_display_name: z.string(),
	literature_piece_display_name: z.string(),
	entity_display_names: z.record(z.string()),
	level: z.string(),
	language_id: z.number(),
	language: z.string(),
	rank: z.number(),
	url: z.string(),
});
export type Document = z.infer<typeof DocumentSchema>;

export const DocumentsAPIResponseSchema = z.object({
	data: z.array(DocumentSchema),
});
export type DocumentsAPIResponse = z.infer<typeof DocumentsAPIResponseSchema>;

export const TermsContentSchema = z.object({
	id: z.string(),
	currentLanguage: z.string(),
	header: z.string(),
	intro: z.string(),
	terms: z.string(),
	rememberSelection: z.boolean(),
	rememberSelectionTitle: z.string(),
	rememberSelectionDescription: z.string(),
	acceptTermsTitle: z.string(),
	acceptTermsDescription: z.string(),
	acceptButtonText: z.string(),
	rejectText: z.string(),
	isVersioned: z.boolean(),
	version: z.number(),
});

export const SiteSchema = z.enum([
	"Intermediaries",
	"Institutional",
	"IndividualInvestors",
	"International",
	"Microsite",
]);
export type Site = z.infer<typeof SiteSchema>;

export const TermsItemSchema = z.object({
	site: SiteSchema,
	persistCookieName: z.string(),
	sessionCookieName: z.string(),
	contentDto: TermsContentSchema,
});

export const TermsApiResponseSchema = z.object({
	statusCode: z.number(),
	result: z.object({
		response: z.array(TermsItemSchema),
	}),
});

export type TermsContent = z.infer<typeof TermsContentSchema>;
export type TermsApiResponse = z.infer<typeof TermsApiResponseSchema>;
export type RedirectMap = Record<Site, string>;

const DatatableStructureContainerSchema = z.object({
	key: z.string(),
	container_name: z.string(),
	container_title: z.string(),
	container_note: z.string(),
	rank: z.number(),
	num_zones: z.number(),
	components: z.array(z.string()),
	lock_to_grid: z.boolean(),
});
export type DatatableStructureContainer = z.infer<
	typeof DatatableStructureContainerSchema
>;

const DatatableStructureComponentSchema = z.object({
	key: z.string(),
	rank: z.number(),
	container: z.string(),
	frontend_component: z.string().nullable(),
	zone: z.number(),
	sum_column: z.string(),
	component_title: z.string(),
	component_note: z.string(),
	datatable: z.string(),
	layout_position: z.null(),
});
export type DatatableStructureComponent = z.infer<
	typeof DatatableStructureComponentSchema
>;

export const DatatableStructureResponseSchema = z.object({
	name: z.string(),
	containers: z.array(DatatableStructureContainerSchema),
	components: z.array(DatatableStructureComponentSchema),
});
export type DatatableStructureResponse = z.infer<
	typeof DatatableStructureResponseSchema
>;

const BreakdownContainerSchema = z.object({
	key: z.string(),
	container_name: z.string(),
	container_title: z.string(),
	rank: z.number(),
	datatables: z.array(z.string()),
});
export type BreakdownContainer = z.infer<typeof BreakdownContainerSchema>;

const ColumnMetadataSchema = z.object({
	key: z.string(),
	header: z.string(),
	display_option: z.string(),
});
export type ColumnMetadata = z.infer<typeof ColumnMetadataSchema>;

const BreakdownDatatableSchema = z.object({
	key: z.string(),
	name: z.string(),
	title: z.string(),
	rank: z.number(),
	container: z.string(),
	include_headers: z.boolean(),
	data_end_date: z.string().nullable(),
	column_metadata: z.array(ColumnMetadataSchema),
	chart_data_column: z.string(),
	data: z.array(z.record(z.string().nullable())),
});
export type BreakdownDatatable = z.infer<typeof BreakdownDatatableSchema>;

export const FundBreakdownsResponseSchema = z.object({
	name: z.string(),
	as_at_date: z.coerce.date(),
	containers: z.array(BreakdownContainerSchema),
	datatables: z.array(BreakdownDatatableSchema),
});
export type FundBreakdownsResponse = z.infer<
	typeof FundBreakdownsResponseSchema
>;
